import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { sectionAnimations } from '@platform/helpers/animations.helper';
import { noop } from 'lodash';

import { EMMAFormElementComponent } from './emma-form-element.component';
import { SelectOption } from '@platform/types';

const VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAMultiSwitchListComponent),
  multi: true,
};

const VALIDATOR = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAMultiSwitchListComponent),
  multi: true,
};

@Component({
    selector: 'emma-multi-switch-list',
    templateUrl: './emma-multi-switch-list.component.html',
    animations: [...sectionAnimations],
    providers: [
        VALUE_ACCESSOR,
        VALIDATOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMAMultiSwitchListComponent,
        },
    ],
    standalone: false
})
export class EMMAMultiSwitchListComponent
  extends EMMAFormElementComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  @Input() items: Array<any> = [];
  @Input() valueKey = 'value';
  @Input() labelKey = 'label';
  @Input() value: Array<string | number> = [];
  @Output() valueChange: EventEmitter<string | number> = new EventEmitter<string | number>();

  @Input() maxLength = Infinity;
  @Input() minLength = 0;

  @Input() hasSelectAll = false;
  @Input() selectAllLabel = $localize`Todo`;
  isAllSelected = false;
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: Array<string | number>) => void = noop;

  override ngOnInit(): void {
    this.detectAllSelected();
  }
  override ngOnChanges(changes: SimpleChanges): void {
    if ('items' in changes || 'value' in changes) {
      this.detectAllSelected();
    }
  }
  onSwitchChanged(isActive: boolean, value: string | number): void {
    const list = new Set(this.value);
    if (isActive) {
      list.add(value);
    } else {
      list.delete(value);
    }
    this.value = Array.from(list);
    this.onTouchedCallback();
    this.onChangeCallback(this.value);
  }
  onSelectAllChanged(isAllSelected: boolean): void {
    if (isAllSelected) {
      this.value = this.items.map((item: string | number | SelectOption) =>
        'object' !== typeof item ? item : item[this.valueKey]
      );
    } else {
      this.value = [];
    }
    this.isAllSelected = isAllSelected;
    this.onTouchedCallback();
    this.onChangeCallback(this.value);
  }

  private detectAllSelected(): void {
    if (this.items?.length && this.value) {
      if (this.items.length === this.value.length) {
        this.isAllSelected = true;
      } else {
        this.isAllSelected = false;
      }
    }
  }

  // From ControlValueAccessor interface
  writeValue(value: Array<string | number>): void {
    this.value = value;
    this.detectAllSelected();
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  // From Validator interface
  override validate: ValidatorFn = (control: AbstractControl) => {
    const validators: ValidatorFn[] = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.maxLength < Infinity) {
      validators.push(Validators.maxLength(this.maxLength));
    }
    if (this.minLength) {
      validators.push(Validators.minLength(this.minLength));
    }
    if (this.validateFn) {
      validators.push(this.validateFn);
    }
    if (validators.length) {
      const validator = Validators.compose(validators);
      return validator ? validator(control) : null;
    }
    return null;
  };
}
